import { Action, ActionType } from './actionTypes';
import { CalculatorState } from './state';

const startingState: CalculatorState = {
    countries: [],
    currencies: [],
    centres: [],
    schools: [],
    bookingSystems: [],
    agents: [],
    agentOffices: [],
    divisions: [],
    isLoadingReferenceData: false,
    agentType: 'Agent',
    selectedProductType: 'Course',
    products: [],
    productSearchTerm: '',
    excludeCommissions: false,
    earlyBirds: false,
    marketDiscount: false,
    pricingScenario: 'New booking',
    isCalculating: false,
};

export function reducer(state: CalculatorState | undefined, action: Action): CalculatorState {
    if (!state) {
        return startingState;
    }

    switch (action.type) {
        case ActionType.PageLoaded:
            return {
                ...startingState,
                countries: action.countries,
                currencies: action.currencies,
                centres: action.centres,
                schools: action.schools,
                bookingSystems: action.bookingSystems,
                agents: action.agents,
                isLoadingReferenceData: false,
            };
        case ActionType.UpdateAge:
            return { ...state, age: action.age };
        case ActionType.UpdateStudentAgeDob:
            return { ...state, age: action.age, dateOfBirth: action.dateOfBirth };
        case ActionType.UpdateAgent:
            return { ...state, agentId: action.agentId, agentOfficeId: undefined, agentOffices: [] };
        case ActionType.UpdateAgentType:
            return { ...state, agentType: action.agentType };
        case ActionType.UpdateCountryOfResidence:
            return { ...state, countryOfResidenceId: action.countryId };
        case ActionType.UpdateDestinationCountry:
            return { ...state, destinationCountryId: action.countryId };
        case ActionType.UpdateBookingCreationDate:
            return { ...state, bookingCreationDate: action.date };
        case ActionType.UpdateCurrency:
            return { ...state, currencyId: action.currencyId };
        case ActionType.UpdateDateFrom:
            const newState = { ...state, dateFrom: action.date };
            if (action.date && state.weeks) {
                const dateFrom = new Date(action.date);
                const dateTo = new Date(dateFrom);
                dateTo.setDate(dateTo.getDate() + (state.weeks * 7));
                newState.dateTo = dateTo.toISOString().split('T')[0];
            }
            return newState;
        case ActionType.UpdateCentre:
            return { ...state, centreId: action.centreId };
        case ActionType.UpdateWeeks: {
            const updatedState = { ...state, weeks: action.weeks };
            if (action.weeks && state.dateFrom) {
                const dateFrom = new Date(state.dateFrom);
                const dateTo = new Date(dateFrom);
                dateTo.setDate(dateTo.getDate() + (action.weeks * 7));
                updatedState.dateTo = dateTo.toISOString().split('T')[0];
            }
            if (action.weeks != null && action.weeks > 0) {
                const weekCount = action.weeks;
                updatedState.products = state.products.map((p) => {
                    const startStr = p.dateFrom || state.dateFrom;
                    if (!startStr) return p;
                    const end = new Date(startStr);
                    end.setDate(end.getDate() + weekCount * 7);
                    return { ...p, dateTo: end.toISOString().split('T')[0] };
                });
            }
            return updatedState;
        }
        case ActionType.UpdateSchool:
            return { ...state, schoolId: action.schoolId };
        case ActionType.UpdateDateTo:
            return { ...state, dateTo: action.date };
        case ActionType.UpdateBookingSystem:
            if (!action.bookingSystemId) {
                return { ...state, bookingSystemId: undefined, divisionId: undefined, divisions: [] };
            }
            return { ...state, bookingSystemId: action.bookingSystemId };
        case ActionType.UpdatePromotionCode:
            return { ...state, promotionCode: action.promotionCode };
        case ActionType.UpdateDivision:
            return { ...state, divisionId: action.divisionId };
        case ActionType.SetDivisions:
            return { ...state, divisions: action.divisions };
        case ActionType.UpdateSelectedProductType:
            return { ...state, selectedProductType: action.productType };
        case ActionType.AddProduct:
            return {
                ...state,
                products: [
                    ...state.products,
                    {
                        ...action.product,
                        dateFrom: state.dateFrom,
                        dateTo: state.dateTo,
                    },
                ],
                productSearchTerm: '',
            };
        case ActionType.RemoveProduct:
            return {
                ...state,
                products: state.products.filter(p => p.id !== action.productId),
            };
        case ActionType.UpdateProductDateFrom:
            return {
                ...state,
                products: state.products.map(p =>
                    p.id === action.productId ? { ...p, dateFrom: action.date } : p
                ),
            };
        case ActionType.UpdateProductDateTo:
            return {
                ...state,
                products: state.products.map(p =>
                    p.id === action.productId ? { ...p, dateTo: action.date } : p
                ),
            };
        case ActionType.UpdateProductSearchTerm:
            return { ...state, productSearchTerm: action.searchTerm };
        case ActionType.UpdateExcludeCommissions:
            return { ...state, excludeCommissions: action.excludeCommissions };
        case ActionType.UpdateEarlyBirds:
            return { ...state, earlyBirds: action.earlyBirds };
        case ActionType.UpdateMarketDiscount:
            return { ...state, marketDiscount: action.marketDiscount };
        case ActionType.UpdatePricingScenario:
            return action.pricingScenario === 'New booking'
                ? {
                    ...state,
                    pricingScenario: action.pricingScenario,
                    amendmentDate: undefined,
                    priceLockToken: undefined,
                }
                : { ...state, pricingScenario: action.pricingScenario };
        case ActionType.UpdateAmendmentDate:
            return { ...state, amendmentDate: action.amendmentDate };
        case ActionType.UpdatePriceLockToken:
            return { ...state, priceLockToken: action.priceLockToken };
        case ActionType.SetSummary:
            return { ...state, summary: action.summary };
        case ActionType.SetCalculating:
            return { ...state, isCalculating: action.isCalculating };
        case ActionType.SetReferenceDataLoading:
            return { ...state, isLoadingReferenceData: action.isLoading };
        case ActionType.ReloadReferenceData:
            return {
                ...state,
                countries: action.countries,
                currencies: action.currencies,
                centres: action.centres,
                schools: action.schools,
                isLoadingReferenceData: false,
            };
        case ActionType.ReloadAgents:
            return {
                ...state,
                agents: action.agents,
            };
        case ActionType.UpdateAgentOffice:
            return { ...state, agentOfficeId: action.agentOfficeId };
        case ActionType.LoadAgentOffices:
            return { ...state, agentOffices: action.agentOffices };
        default:
            return state;
    }
}

export const reducers = {
    reducer: reducer,
};